/**
 * The whole prompt, on a sheet.
 *
 * The seed's caption shows a short title drawn from the prompt
 * (`canvas/seedCaption.ts`); this is where all of it is. It is deliberately a
 * READING surface and nothing else: no editor, because a rewrite of the seed is
 * the thread's E key with its own re-derive pass and its own price, and a sheet
 * that also edited would be a second door to that pass with none of its
 * pricing. One paragraph, as typed — `white-space: pre-wrap`, so a numbered
 * brief keeps its numbers on their own lines, which the caption's single
 * wrapped paragraph could not give it.
 *
 * Same shell as every other sheet: the scrim, `useModalFocus`, and a rung on
 * the Escape ladder in `App.tsx`, which also names it in every "is a sheet
 * open" guard — the `?` key must not open the keyboard reference over this, or
 * the second sheet's cleanup would clear `inert` under the first.
 */

import { useRef, type RefObject } from 'react'
import { useStore } from '../core/store'
import { claimRestatesLabel, type ReasonNode } from '../core/types'
import { Icon } from './components/Icon'
import { useModalFocus } from './components/useModalFocus'

/** The seed — the one node with no parent — or null on a blank canvas. */
export function seedOf(s: {
  session: { order: string[]; nodes: Record<string, ReasonNode | undefined> }
}): ReasonNode | null {
  for (const id of s.session.order) {
    const n = s.session.nodes[id]
    if (n && n.parentId === null) return n
  }
  return null
}

async function copyPrompt(text: string) {
  const st = useStore.getState()
  try {
    await navigator.clipboard.writeText(text)
    st.toast('Copied the prompt.', 'good')
  } catch {
    st.toast('Clipboard blocked — select the text and copy it instead.', 'warn')
  }
}

/**
 * The sheet itself, pure: everything it prints arrives as props, so a test can
 * render it without a store behind it (`PromptSheet.test.tsx`). The connected
 * shell below is what the app mounts.
 */
export function PromptSheetBody({
  seed,
  onClose,
  dialogRef,
}: {
  seed: ReasonNode
  onClose: () => void
  dialogRef?: RefObject<HTMLDivElement | null>
}) {
  return (
    <div
      className="scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="sheet sheet--tall panel"
        role="dialog"
        aria-modal="true"
        aria-label="The prompt"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="pathways" size={18} strokeWidth={1.5} style={{ color: 'var(--accent)' }} />
          <h2>The prompt</h2>
        </div>
        <p className="gnote">
          The seed of this exploration, exactly as it was typed. The canvas shows its opening words;
          this is all of it.
        </p>
        <div className="sheet-body scroll">
          <p className="promptsheet-text">{seed.label}</p>
          {/* The seed's claim is its label unless a rewrite made them differ —
              the same rule the thread's cards read (D90), so the sheet never
              prints the prompt twice and never hides a claim that continues
              past it. */}
          {!claimRestatesLabel(seed) && <p className="promptsheet-text">{seed.claim}</p>}
        </div>
        <div className="sheet-actions">
          <button className="btn" onClick={() => void copyPrompt(seed.label)}>
            <Icon name="copy" size={13} />
            Copy
          </button>
          <button className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

export function PromptSheet() {
  const open = useStore((s) => s.promptOpen)
  const setOpen = useStore((s) => s.setPromptOpen)
  const seed = useStore(seedOf)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const showing = open && seed !== null
  useModalFocus(dialogRef, showing)

  if (!showing) return null
  return <PromptSheetBody seed={seed} onClose={() => setOpen(false)} dialogRef={dialogRef} />
}
